/**
 * A message's documents — PDFs, sheets, archives, anything the media grid has no picture
 * for — as one full-width row each.
 *
 * They used to share the grid with photos and videos, where a document became a 107x80
 * icon tile with its name clipped to two lines and no room for its size. A document is
 * identified by its name and weight, so it gets the width to show both.
 *
 * A row opens the same `LightBox` the grid opens, so a PDF still previews in place and
 * anything the viewer cannot render still offers its download.
 */

import type { GetChannelsMessagesByCidItem } from "@birdwatch/client";
import { cn } from "cn";
import { isEmpty } from "lodash-es";
import { useMemo, useState } from "react";
import { AttachmentTypeIcon } from "@/components/ui/attachment-new";
import { LightBox } from "@/components/ui/lightbox";
import { isAttachmentUploaded } from "@/libs/attachment-visibility";
import {
  formatBytes,
  resolveAttachmentFileExtension,
} from "@/libs/file-helper";
import { messageCardClassName } from "./message-card";

export type MessageDocumentListProps = {
  attachments: GetChannelsMessagesByCidItem["attachments"];
  /**
   * The containing bubble's surface for each row's shell. Applied last.
   *
   * It lands on the rows rather than on this wrapper because the wrapper carries no
   * surface of its own — same hand-off as `ChannelBlock`'s `cardClassName`.
   */
  rowClassName?: string;
};

type AttachmentItem = NonNullable<
  NonNullable<MessageDocumentListProps["attachments"]>[number]
>;

export function MessageDocumentList({
  attachments,
  rowClassName,
}: MessageDocumentListProps) {
  const [attachmentIndex, setAttachmentIndex] = useState<number | undefined>(
    undefined,
  );

  // A message can be sent while an attachment is still uploading — those rows are not
  // openable yet. See `isAttachmentUploaded` for why `contentUri` cannot decide this.
  const documents = useMemo(() => {
    return (attachments ?? []).filter((attachment: AttachmentItem) =>
      isAttachmentUploaded(attachment),
    );
  }, [attachments]);

  if (isEmpty(documents)) {
    return null;
  }

  return (
    // `mt-1` matches `MessageAttachment`, the neighbour this list sits beside in the
    // classic row. The conversation bubble spaces its own children and strips it.
    <div className="mt-1 flex w-full flex-col gap-0.5">
      {documents.map((attachment: AttachmentItem, index: number) => {
        const fileName = attachment.fileName || "";
        const fileSize = Number(attachment.fileSize) || 0;
        // An uploaded row can still carry no size (`isAttachmentUploaded` accepts
        // `uploaded: true` on its own), and "0 B" would be a wrong answer rather than a
        // missing one — the type is what is left to say about the file.
        const meta =
          fileSize > 0
            ? formatBytes(fileSize)
            : resolveAttachmentFileExtension({
                fileName,
                contentUri: attachment.contentUri,
              }).toUpperCase();

        return (
          <button
            key={attachment.id ?? index}
            type="button"
            data-test="message-document-row"
            onClick={() => setAttachmentIndex(index)}
            className={cn(
              /* sorted */
              messageCardClassName,
              "flex items-center gap-2",
              "px-3 py-2",
              "text-left",
              "cursor-pointer",
              "transition-colors",
              rowClassName,
            )}
          >
            <AttachmentTypeIcon
              fileName={fileName}
              size={32}
              className="shrink-0"
            />

            <div className="flex min-w-0 flex-1 flex-col">
              <p
                className="truncate text-sm text-text-dark-max"
                title={fileName}
              >
                {fileName}
              </p>
              {!!meta && (
                <p className="truncate text-xs text-text-dark-high">{meta}</p>
              )}
            </div>
          </button>
        );
      })}

      <LightBox
        open={attachmentIndex !== undefined}
        slideIndex={attachmentIndex}
        attachments={documents}
        onClose={() => setAttachmentIndex(undefined)}
      />
    </div>
  );
}
